/**
 * Builds evenly spaced tick values with a "nice" step (1, 2, 5 × 10ⁿ) so gridlines stay at round numbers
 * relative to the data span (e.g. tens for ~100s, larger steps for ~10⁴).
 *
 * @param {number} min - Data minimum (axis lower bound will be ≤ this).
 * @param {number} max - Data maximum (axis upper bound will be ≥ this).
 * @param {number} [maxTicks=10] - Target maximum number of tick marks (including ends).
 * @returns {{ scaleMin: number, scaleMax: number, ticks: number[], step: number }}
 */
export const buildNiceLinearTicks = (min, max, maxTicks = 10) => {
    const lo = Math.min(min, max);
    const hi = Math.max(min, max);
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
        return { scaleMin: 0, scaleMax: 1, ticks: [0, 1], step: 1 };
    }
    if (hi <= lo) {
        const pad = lo === 0 ? 1 : Math.abs(lo) * 0.05 || 1;
        return buildNiceLinearTicks(lo - pad, hi + pad, maxTicks);
    }

    const range = hi - lo;
    const rough = range / Math.max(2, maxTicks - 1);
    const log = Math.log10(rough);
    const pow10 = 10 ** Math.floor(log);
    const n = rough / pow10;
    let base = 10;
    if (n <= 1) base = 1;
    else if (n <= 2) base = 2;
    else if (n <= 5) base = 5;
    else base = 10;

    let step = base * pow10;

    for (let guard = 0; guard < 24; guard += 1) {
        // Align [lo, hi] to the step grid. Do not use `floor(lo/step - ε)` — when lo is 0, ε makes the
        // argument slightly negative so floor becomes -1 and scaleMin jumps to -step (spurious negative gridlines).
        let scaleMin = Math.floor(lo / step) * step;
        if (scaleMin > lo) scaleMin -= step;
        let scaleMax = Math.ceil(hi / step) * step;
        if (scaleMax < hi) scaleMax += step;

        const ticks = [];
        for (let x = scaleMin; x <= scaleMax + step * 1e-9; x += step) {
            ticks.push(Number.parseFloat(Number(x).toPrecision(12)));
        }

        if (ticks.length <= maxTicks + 1 || step >= range) {
            return { scaleMin, scaleMax, ticks, step };
        }
        step *= 2;
    }

    const fallback = { scaleMin: lo, scaleMax: hi, ticks: [lo, hi], step: hi - lo };
    return fallback;
};
